import {
  ParticipantRoleSchema,
  ParticipantStatusSchema,
  type IncidentWorkItemParticipantEnhanced,
  type IncidentWorkItemWithContext,
} from "../../types/incidentWorkItem.js";
import type { UserIdUnbranded } from "../../types/user.js";

/**
 * The officer responsible for an incident.
 *
 * `role` alone identifies them, with no reference to `status`: every transition
 * that hands responsibility on also demotes the officer losing it to
 * `participant`, so an incident has exactly one `owner` participant in every
 * state — including while a transfer is pending, which the product treats as
 * still the sending officer's responsibility.
 *
 * Do not confuse this with whether a user has any live interest in an incident.
 * A pending owner, and an officer who has been transferred away, are both
 * legitimately involved without being the owner.
 */
export function getCurrentOwner(
  incidentWorkItem: IncidentWorkItemWithContext,
): IncidentWorkItemParticipantEnhanced | undefined {
  return incidentWorkItem.participants.find(
    (participant) => participant.role === ParticipantRoleSchema.enum.owner,
  );
}

export function isCurrentOwner(
  incidentWorkItem: IncidentWorkItemWithContext,
  userId: UserIdUnbranded | undefined,
): boolean {
  if (userId === undefined) return false;
  return getCurrentOwner(incidentWorkItem)?.participantId === userId;
}

/**
 * Whether this user is the officer a transfer is currently pending for, and so
 * the only person who may accept or reject it.
 */
export function isPendingOwner(
  incidentWorkItem: IncidentWorkItemWithContext,
  userId: UserIdUnbranded | undefined,
): boolean {
  if (userId === undefined) return false;
  return incidentWorkItem.participants.some(
    (participant) =>
      participant.participantId === userId &&
      participant.role === ParticipantRoleSchema.enum.pendingOwner &&
      participant.status === ParticipantStatusSchema.enum.pendingTransfer,
  );
}

/**
 * Whether this user is an officer a completed transfer moved off an incident.
 *
 * Narrower than not being the owner: a supervisor or an admin never held the
 * incident, and so was never party to a transfer away from it.
 */
export function wasTransferredAway(
  incidentWorkItem: IncidentWorkItemWithContext,
  userId: UserIdUnbranded | undefined,
): boolean {
  if (userId === undefined) return false;
  return incidentWorkItem.participants.some(
    (participant) =>
      participant.participantId === userId &&
      participant.status === ParticipantStatusSchema.enum.transferred,
  );
}
